import {
  Workflow,
  WorkflowSummary,
  WorkflowCreate,
  WorkflowUpdate,
  WorkflowExecution,
  ExecutionCreate,
  ExecutionSummary,
  ScriptMetadata,
  PaginatedResponse
} from '../types/workflow';
import {
  TradeSparqResponse,
  TradeFilterOptions,
  DownloadParams
} from '../types/tradesparq';
import {
  BOMExtractionJob,
  BOMJobListResponse,
  BOMExtractionParams,
  BOMExportOptions,
  BOMFilterOptions,
  BOMStatistics,
  BOMAnalytics,
  BOMItem
} from '../types/bom';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchApi<T>(
  endpoint: string, 
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new ApiError(response.status, errorText || `HTTP ${response.status}`);
  }

  return response.json();
}

// Workflow API
export const workflowApi = {
  // Get all workflows
  getWorkflows: async (): Promise<WorkflowSummary[]> => {
    return fetchApi<WorkflowSummary[]>('/api/v1/workflows/');
  },

  // Get workflow by ID
  getWorkflow: async (workflowId: string): Promise<Workflow> => {
    return fetchApi<Workflow>(`/api/v1/workflows/${workflowId}`);
  },

  // Create new workflow
  createWorkflow: async (workflow: WorkflowCreate): Promise<Workflow> => {
    return fetchApi<Workflow>('/api/v1/workflows/', {
      method: 'POST',
      body: JSON.stringify(workflow),
    });
  },

  // Update workflow
  updateWorkflow: async (workflowId: string, updates: WorkflowUpdate): Promise<Workflow> => {
    return fetchApi<Workflow>(`/api/v1/workflows/${workflowId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  // Delete workflow
  deleteWorkflow: async (workflowId: string): Promise<void> => {
    return fetchApi<void>(`/api/v1/workflows/${workflowId}`, {
      method: 'DELETE',
    });
  },

  // Import workflow
  importWorkflow: async (file: File): Promise<Workflow> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_BASE_URL}/api/v1/workflows/import`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(response.status, errorText || `HTTP ${response.status}`);
    }

    return response.json();
  },

  // Export workflow
  exportWorkflow: async (workflowId: string): Promise<Blob> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/workflows/${workflowId}/export`);
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(response.status, errorText || `HTTP ${response.status}`);
    }

    return response.blob();
  },
};

// Execution API
export const executionApi = {
  // Get all executions
  getExecutions: async (
    limit: number = 50, 
    offset: number = 0
  ): Promise<PaginatedResponse<ExecutionSummary>> => {
    return fetchApi<PaginatedResponse<ExecutionSummary>>(
      `/api/v1/executions/?limit=${limit}&offset=${offset}`
    );
  },

  // Get execution by ID
  getExecution: async (executionId: string): Promise<WorkflowExecution> => {
    return fetchApi<WorkflowExecution>(`/api/v1/executions/${executionId}`);
  },

  // Create new execution
  createExecution: async (execution: ExecutionCreate): Promise<WorkflowExecution> => {
    return fetchApi<WorkflowExecution>('/api/v1/executions/', {
      method: 'POST',
      body: JSON.stringify(execution),
    });
  },

  // Cancel execution
  cancelExecution: async (executionId: string): Promise<WorkflowExecution> => {
    return fetchApi<WorkflowExecution>(`/api/v1/executions/${executionId}/cancel`, {
      method: 'POST',
    });
  },

  // Get execution logs
  getExecutionLogs: async (executionId: string): Promise<string[]> => {
    return fetchApi<string[]>(`/api/v1/executions/${executionId}/logs`);
  },
};

// Scripts API
export const scriptsApi = {
  // Get all scripts
  getScripts: async (): Promise<ScriptMetadata[]> => {
    return fetchApi<ScriptMetadata[]>('/api/v1/scripts/');
  },

  // Get script by path
  getScript: async (scriptPath: string): Promise<ScriptMetadata> => {
    const encodedPath = encodeURIComponent(scriptPath);
    return fetchApi<ScriptMetadata>(`/api/v1/scripts/${encodedPath}`);
  },

  // Validate script
  validateScript: async (scriptPath: string): Promise<{ valid: boolean; errors: string[] }> => {
    const encodedPath = encodeURIComponent(scriptPath);
    return fetchApi<{ valid: boolean; errors: string[] }>(
      `/api/v1/scripts/${encodedPath}/validate`
    );
  },
};

// Files API
export const filesApi = {
  // Upload file
  uploadFile: async (file: File, path?: string): Promise<{ filename: string; path: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    if (path) {
      formData.append('path', path);
    }
    
    const response = await fetch(`${API_BASE_URL}/api/v1/files/upload`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(response.status, errorText || `HTTP ${response.status}`);
    }

    return response.json();
  },

  // Download file
  downloadFile: async (filePath: string): Promise<Blob> => {
    const encodedPath = encodeURIComponent(filePath);
    const response = await fetch(`${API_BASE_URL}/api/v1/files/download/${encodedPath}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(response.status, errorText || `HTTP ${response.status}`);
    }

    return response.blob();
  },

  // List files
  listFiles: async (directory?: string): Promise<string[]> => {
    const params = directory ? `?directory=${encodeURIComponent(directory)}` : '';
    return fetchApi<string[]>(`/api/v1/files/list${params}`);
  },

  // Delete file
  deleteFile: async (filePath: string): Promise<void> => {
    const encodedPath = encodeURIComponent(filePath);
    return fetchApi<void>(`/api/v1/files/${encodedPath}`, {
      method: 'DELETE',
    });
  },
};

// WebSocket connection for real-time updates
export class WebSocketManager {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;

  constructor(private url: string = 'ws://localhost:8000/api/v1/ws') {}

  connect(onMessage: (data: any) => void, onError?: (error: Event) => void): void {
    try {
      this.ws = new WebSocket(this.url);
      
      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.reconnectAttempts = 0;
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          onMessage(data);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onclose = () => {
        console.log('WebSocket disconnected');
        this.attemptReconnect(onMessage, onError);
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        if (onError) {
          onError(error);
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      if (onError) {
        onError(error as Event);
      }
    }
  }

  private attemptReconnect(onMessage: (data: any) => void, onError?: (error: Event) => void): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      setTimeout(() => {
        console.log(`Attempting to reconnect... (${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);
        this.reconnectAttempts++;
        this.connect(onMessage, onError);
      }, this.reconnectDelay * this.reconnectAttempts);
    }
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  send(data: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('WebSocket is not connected');
    }
  }
}

// Tradesparq API
export const tradesparqApi = {
  // Get buyer data
  getBuyerData: async (filters: Partial<TradeFilterOptions>): Promise<TradeSparqResponse> => {
    const params = new URLSearchParams();
    
    // Add all filter parameters
    if (filters.buyer_t) params.append('buyer_t', filters.buyer_t);
    if (filters.hs_code) params.append('hs_code', filters.hs_code);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.max_records) params.append('max_records', filters.max_records.toString());
    if (filters.prod_desc) params.append('prod_desc', filters.prod_desc);
    if (filters.force_refresh) params.append('force_refresh', filters.force_refresh.toString());
    if (filters.select_cols) params.append('select_cols', filters.select_cols);
    if (filters.data_type) params.append('data_type', filters.data_type);
    if (filters.freq) params.append('freq', filters.freq);
    if (filters.page_size) params.append('page_size', filters.page_size.toString());
    if (filters.concurrency) params.append('concurrency', filters.concurrency.toString());
    if (filters.do_topup_pass !== undefined) params.append('do_topup_pass', filters.do_topup_pass.toString());

    const queryString = params.toString();
    return fetchApi<TradeSparqResponse>(`/api/v1/tradesparq/buyer/?${queryString}`);
  },

  // Get supplier data
  getSupplierData: async (filters: Partial<TradeFilterOptions>): Promise<TradeSparqResponse> => {
    const params = new URLSearchParams();
    
    // Add all filter parameters
    if (filters.supplier_t) params.append('supplier_t', filters.supplier_t);
    if (filters.hs_code) params.append('hs_code', filters.hs_code);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.max_records) params.append('max_records', filters.max_records.toString());
    if (filters.prod_desc) params.append('prod_desc', filters.prod_desc);
    if (filters.force_refresh) params.append('force_refresh', filters.force_refresh.toString());
    if (filters.select_cols) params.append('select_cols', filters.select_cols);
    if (filters.data_type) params.append('data_type', filters.data_type);
    if (filters.freq) params.append('freq', filters.freq);
    if (filters.page_size) params.append('page_size', filters.page_size.toString());
    if (filters.concurrency) params.append('concurrency', filters.concurrency.toString());
    if (filters.do_topup_pass !== undefined) params.append('do_topup_pass', filters.do_topup_pass.toString());

    const queryString = params.toString();
    return fetchApi<TradeSparqResponse>(`/api/v1/tradesparq/supplier/?${queryString}`);
  },

  // Download buyer data as CSV
  downloadBuyerData: async (params: DownloadParams): Promise<Blob> => {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const response = await fetch(`${API_BASE_URL}/api/v1/tradesparq/download/buyer/data?${searchParams}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(response.status, errorText || `HTTP ${response.status}`);
    }

    return response.blob();
  },

  // Download supplier data as CSV
  downloadSupplierData: async (params: DownloadParams): Promise<Blob> => {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const response = await fetch(`${API_BASE_URL}/api/v1/tradesparq/download/supplier/data?${searchParams}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(response.status, errorText || `HTTP ${response.status}`);
    }

    return response.blob();
  },

  // Download buyer statistics as CSV
  downloadBuyerStats: async (params: DownloadParams): Promise<Blob> => {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const response = await fetch(`${API_BASE_URL}/api/v1/tradesparq/download/buyer/stats?${searchParams}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(response.status, errorText || `HTTP ${response.status}`);
    }

    return response.blob();
  },

  // Download supplier statistics as CSV
  downloadSupplierStats: async (params: DownloadParams): Promise<Blob> => {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const response = await fetch(`${API_BASE_URL}/api/v1/tradesparq/download/supplier/stats?${searchParams}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(response.status, errorText || `HTTP ${response.status}`);
    }

    return response.blob();
  },

  // Semantic search on cached data
  semanticSearch: async (cacheKey: string, query: string, options: {
    similarity_threshold?: number;
    rerank_threshold?: number;
    top_k?: number;
    column_to_search?: string;
    rerank_batch_size?: number;
  } = {}): Promise<TradeSparqResponse> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/tradesparq/search/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        cache_key: cacheKey,
        query,
        similarity_threshold: options.similarity_threshold || 0.25,
        rerank_threshold: options.rerank_threshold || 0.09,
        top_k: options.top_k,
        column_to_search: options.column_to_search || 'prod_desc',
        rerank_batch_size: options.rerank_batch_size || 8,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(response.status, errorText || `HTTP ${response.status}`);
    }

    return response.json();
  },
};

// BOM API
export const bomApi = {
  // Submit BOM extraction job
  submitExtractionJob: async (params: BOMExtractionParams): Promise<{ job_id: string; status: string; created_at: string; filenames: string[]; status_url: string }> => {
    const formData = new FormData();
    
    // Add files
    params.files.forEach(file => {
      formData.append('files', file);
    });
    
    // Add parameters
    formData.append('extraction_method', params.extraction_method);
    formData.append('use_llm_mapper', params.use_llm_mapper.toString());
    formData.append('enable_llm_validation', params.enable_llm_validation.toString());
    formData.append('enable_bom_reasoning', params.enable_bom_reasoning.toString());
    formData.append('llm_provider', params.llm_provider);

    const response = await fetch(`${API_BASE_URL}/api/v1/bom/jobs/extract`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(response.status, errorText || `HTTP ${response.status}`);
    }

    return response.json();
  },

  // Get job status and results
  getJobStatus: async (jobId: string): Promise<BOMExtractionJob> => {
    return fetchApi<BOMExtractionJob>(`/api/v1/bom/jobs/${jobId}`);
  },

  // List all jobs with optional filtering
  listJobs: async (filters?: BOMFilterOptions): Promise<BOMJobListResponse> => {
    const params = new URLSearchParams();
    
    if (filters?.status) params.append('status', filters.status);
    if (filters?.extraction_method) params.append('extraction_method', filters.extraction_method);
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.min_records) params.append('min_records', filters.min_records.toString());
    if (filters?.max_records) params.append('max_records', filters.max_records.toString());
    if (filters?.search) params.append('search', filters.search);

    const queryString = params.toString();
    return fetchApi<BOMJobListResponse>(`/api/v1/bom/jobs${queryString ? `?${queryString}` : ''}`);
  },

  // Get BOM statistics
  getStatistics: async (): Promise<BOMStatistics> => {
    return fetchApi<BOMStatistics>('/api/v1/bom/statistics');
  },

  // Get BOM analytics for a specific job
  getAnalytics: async (jobId: string): Promise<BOMAnalytics> => {
    return fetchApi<BOMAnalytics>(`/api/v1/bom/jobs/${jobId}/analytics`);
  },

  // Export BOM data
  exportData: async (jobId: string, options: BOMExportOptions): Promise<Blob> => {
    const params = new URLSearchParams();
    params.append('format', options.format);
    params.append('include_metadata', options.include_metadata.toString());
    if (options.selected_files) {
      params.append('selected_files', options.selected_files.join(','));
    }

    const response = await fetch(`${API_BASE_URL}/api/v1/bom/jobs/${jobId}/export?${params}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(response.status, errorText || `HTTP ${response.status}`);
    }

    return response.blob();
  },

  // Delete a job
  deleteJob: async (jobId: string): Promise<void> => {
    return fetchApi<void>(`/api/v1/bom/jobs/${jobId}`, {
      method: 'DELETE',
    });
  },

  // Update BOM item
  updateBOMItem: async (jobId: string, itemId: string, item: Partial<BOMItem>): Promise<BOMItem> => {
    return fetchApi<BOMItem>(`/api/v1/bom/jobs/${jobId}/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(item),
    });
  },

  // Delete BOM item
  deleteBOMItem: async (jobId: string, itemId: string): Promise<void> => {
    return fetchApi<void>(`/api/v1/bom/jobs/${jobId}/items/${itemId}`, {
      method: 'DELETE',
    });
  },

  // Add new BOM item
  addBOMItem: async (jobId: string, item: Omit<BOMItem, 'id' | 'created_at' | 'updated_at'>): Promise<BOMItem> => {
    return fetchApi<BOMItem>(`/api/v1/bom/jobs/${jobId}/items`, {
      method: 'POST',
      body: JSON.stringify(item),
    });
  },

  // Health check
  healthCheck: async (): Promise<{ status: string; version: string; active_jobs: number; completed_jobs: number; failed_jobs: number }> => {
    return fetchApi<{ status: string; version: string; active_jobs: number; completed_jobs: number; failed_jobs: number }>('/api/v1/bom/health');
  },
};

export { ApiError };